import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import moment from "moment";
import {
  leavePalette,
  cardSx,
  canonicalStatus,
  statusMeta,
} from "../AttendanceCorrection/attendanceCorrectionHelpers";

// Shift Change reuses the exact tokens + status helpers the Attendance
// Correction screen established, so both reads of My Requests > Attendance &
// Workday render as one system.
export { leavePalette, cardSx, canonicalStatus, statusMeta };

// Per-shift-change-type presentation (chip label + accent). `matches` isn't
// needed yet (no API), but the shape mirrors CORRECTION_TYPES so wiring a
// backend later is a drop-in.
export const SHIFT_CORRECTION_TYPES = {
  wrong_shift: {
    key: "wrong_shift",
    label: "Wrong Shift Assigned",
    color: leavePalette.blue,
    icon: SwapHorizOutlinedIcon,
  },
  shift_swap: {
    key: "shift_swap",
    label: "Shift Swap",
    color: leavePalette.purple,
    icon: SwapHorizOutlinedIcon,
  },
  missing_shift: {
    key: "missing_shift",
    label: "Shift Not Assigned",
    color: leavePalette.amber,
    icon: ScheduleOutlinedIcon,
  },
  shift_timing: {
    key: "shift_timing",
    label: "Shift Timing Change",
    color: leavePalette.green,
    icon: ScheduleOutlinedIcon,
  },
};

export const SHIFT_CORRECTION_TYPE_OPTIONS = Object.values(
  SHIFT_CORRECTION_TYPES,
).map((t) => ({ id: t.key, label: t.label }));

export const SCHEDULE_REQUEST_TYPES = {
  shift_change: {
    key: "shift_change",
    label: "Shift Change",
    color: leavePalette.purple,
    icon: SwapHorizOutlinedIcon,
  },
  week_off_swap: {
    key: "week_off_swap",
    label: "Week Off Swap",
    color: "#F97316",
    icon: CalendarMonthOutlinedIcon,
  },
};

export const scheduleRequestTypeMeta = (typeKey) =>
  SCHEDULE_REQUEST_TYPES[typeKey] || SCHEDULE_REQUEST_TYPES.shift_change;

export const shiftCorrectionMeta = (typeKey) =>
  SHIFT_CORRECTION_TYPES[typeKey] || {
    key: typeKey,
    label: typeKey || "—",
    color: leavePalette.gray,
    icon: ScheduleOutlinedIcon,
  };

// Column definitions shared by the CustomFilter toolbar and the history table.
export const SHIFT_CORRECTION_HEADERS = [
  { name: "Type", key: "request_type_label", type: "string" },
  { name: "Date", key: "date_display", type: "date" },
  { name: "Original Shift", key: "original_shift", type: "string" },
  { name: "New Shift", key: "corrected_shift", type: "string" },
  { name: "Reason", key: "reason", type: "string" },
  { name: "Status", key: "status", type: "autocomplete" },
  { name: "Applied On", key: "applied_display", type: "date" },
];

export const formatShiftDateRange = (start, end) => {
  const startDate = start ? moment(start) : null;
  if (!startDate || !startDate.isValid()) return "";
  const endDate = end ? moment(end) : null;
  if (!endDate || !endDate.isValid() || endDate.isSame(startDate, "day")) {
    return startDate.format("DD MMM YYYY");
  }
  return `${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`;
};

export const formatShiftDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(
    value,
    ["DD-MM-YYYY, HH:mm", "DD-MM-YYYY, hh:mm A", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
};

const stripEmployeeIdFromName = (value, employeeId) => {
  if (!value) return value;
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return text;

  if (employeeId) {
    return text
      .replace(new RegExp(`\\s*\\(?${String(employeeId)}\\)?$`, "i"), "")
      .trim();
  }

  return text.replace(/\s*\(?[A-Z]*\d{2,}\)?$/i, "").trim();
};

export const cleanShiftDisplayName = (value, employeeId) =>
  stripEmployeeIdFromName(value, employeeId) || value;

export const normalizeWeekOffLabel = (value) => {
  if (!value) return value;
  const text = String(value).trim();
  if (/^(weekoff|weekly off|week-off)$/i.test(text)) return "Week Off";
  return text.replace(/\bWeekly Off\b/gi, "Week Off");
};

// Normalize a record for the shared filter/search/sort: every visible column
// must address a flat row[key].
export const toShiftFilterRow = (r) => ({
  ...r,
  correction_label: shiftCorrectionMeta(r.correction_type).label,
  request_type_label: scheduleRequestTypeMeta(r.request_type).label,
  date_display:
    r.request_type === "week_off_swap" && r.date && r.swap_date
      ? `${moment(r.date).format("DD MMM YYYY")} <-> ${moment(r.swap_date).format("DD MMM YYYY")}`
      : formatShiftDateRange(r.date, r.to_date),
  applied_display: formatShiftDateTime(r.applied_on),
});

// Backend endpoint:
//   GET my_zone/apply/shift_change/myzone-shift-change/?user_id=<id>
// Map an API row (see OrganServer_Tenant/My_Zone/Apply/Shift_Change/views.py)
// to the flat record shape every consumer on this screen already reads. The
// API sends `date` as "DD-MM-YYYY" and `applied_on` as "DD-MM-YYYY, HH:mm";
// normalize both to values `toShiftFilterRow` / the table can format.
export const toShiftChangeRecord = (r = {}) => ({
  ...r,
  id: r.id,
  request_id: r.id ? `SCR-${String(r.id).padStart(4, "0")}` : undefined,
  request_type: r.request_type || "shift_change",
  date: r.date ? moment(r.date, "DD-MM-YYYY").format("YYYY-MM-DD") : null,
  swap_date: r.swap_date ? moment(r.swap_date, "DD-MM-YYYY").format("YYYY-MM-DD") : null,
  to_date: undefined,
  // The model does not track a change-type; leave it unset so the "Change
  // Type" column renders the neutral "—" from shiftCorrectionMeta.
  correction_type: undefined,
  original_shift: normalizeWeekOffLabel(r.assigned_shift) || "—",
  corrected_shift: normalizeWeekOffLabel(r.requested_shift) || "—",
  reason: r.reason,
  status: r.status,
  applied_on: r.applied_on
    ? moment(r.applied_on, "DD-MM-YYYY, HH:mm").toISOString()
    : null,
  employee_name: cleanShiftDisplayName(r.employee_name, r.employee_id),
  applied_by:
    cleanShiftDisplayName(r.applied_by, r.employee_id) ||
    cleanShiftDisplayName(r.employee_name, r.employee_id) ||
    cleanShiftDisplayName(r.user_name, r.employee_id) ||
    cleanShiftDisplayName(r.username, r.employee_id) ||
    cleanShiftDisplayName(r.requested_by, r.employee_id),
  approved_by: cleanShiftDisplayName(r.approved_by, r.approver_id),
  approved_at: r.approved_at || r.approved_on,
  rejected_by: cleanShiftDisplayName(r.rejected_by, r.rejected_by_id),
  rejected_at: r.rejected_at || r.rejected_on,
  cancelled_by: cleanShiftDisplayName(
    r.cancelled_by || r.canceled_by,
    r.cancelled_by_id || r.canceled_by_id,
  ),
  cancelled_at: r.cancelled_at || r.cancelled_on || r.canceled_at || r.canceled_on,
  cancelled_reason: r.cancelled_reason || r.cancel_reason || r.canceled_reason,
});

// --- Demo dataset ---------------------------------------------------------
// Retained only as a shape reference; the screen now reads the live API via
// `toShiftChangeRecord`. Safe to delete once no longer referenced.
export const SEED_SHIFT_CORRECTIONS = [
  { id: 12, request_id: "SCR-2026-0012", date: "2026-08-21", correction_type: "wrong_shift", original_shift: "Night Shift (22:00 - 06:00)", corrected_shift: "Standard Shift (09:30 - 18:30)", reason: "Assigned to night shift by mistake", status: "Pending", applied_on: "2026-08-20T10:15:00" },
  { id: 11, request_id: "SCR-2026-0011", date: "2026-08-19", correction_type: "shift_swap", original_shift: "Morning Shift (06:00 - 14:00)", corrected_shift: "Evening Shift (14:00 - 22:00)", reason: "Swapped with colleague", status: "Approved", applied_on: "2026-08-19T14:30:00" },
  { id: 10, request_id: "SCR-2026-0010", date: "2026-08-18", correction_type: "shift_timing", original_shift: "General Shift (10:00 - 19:00)", corrected_shift: "Standard Shift (09:30 - 18:30)", reason: "Shift timing changed for the day", status: "Approved", applied_on: "2026-08-18T11:05:00" },
  { id: 9, request_id: "SCR-2026-0009", date: "2026-08-16", correction_type: "missing_shift", original_shift: "—", corrected_shift: "Standard Shift (09:30 - 18:30)", reason: "No shift was assigned", status: "Pending", applied_on: "2026-08-16T19:45:00" },
  { id: 8, request_id: "SCR-2026-0008", date: "2026-08-14", correction_type: "wrong_shift", original_shift: "Evening Shift (14:00 - 22:00)", corrected_shift: "Morning Shift (06:00 - 14:00)", reason: "Roster error", status: "Rejected", applied_on: "2026-08-15T09:20:00" },
  { id: 7, request_id: "SCR-2026-0007", date: "2026-08-12", correction_type: "shift_swap", original_shift: "Standard Shift (09:30 - 18:30)", corrected_shift: "General Shift (10:00 - 19:00)", reason: "Mutual swap approved by lead", status: "Approved", applied_on: "2026-08-12T18:15:00" },
  { id: 6, request_id: "SCR-2026-0006", date: "2026-08-10", correction_type: "wrong_shift", original_shift: "Morning Shift (06:00 - 14:00)", corrected_shift: "Standard Shift (09:30 - 18:30)", reason: "Wrong shift on roster", status: "Cancelled", applied_on: "2026-08-10T15:40:00" },
  { id: 5, request_id: "SCR-2026-0005", date: "2026-08-08", correction_type: "shift_timing", original_shift: "Standard Shift (09:30 - 18:30)", corrected_shift: "General Shift (10:00 - 19:00)", reason: "Late start approved", status: "Pending", applied_on: "2026-08-08T11:30:00" },
  { id: 4, request_id: "SCR-2026-0004", date: "2026-08-07", correction_type: "shift_swap", original_shift: "Evening Shift (14:00 - 22:00)", corrected_shift: "Morning Shift (06:00 - 14:00)", reason: "Swapped shift", status: "Approved", applied_on: "2026-08-07T17:40:00" },
  { id: 3, request_id: "SCR-2026-0003", date: "2026-08-05", correction_type: "missing_shift", original_shift: "—", corrected_shift: "Standard Shift (09:30 - 18:30)", reason: "Shift missing after transfer", status: "Rejected", applied_on: "2026-08-05T20:20:00" },
  { id: 2, request_id: "SCR-2026-0002", date: "2026-08-04", correction_type: "wrong_shift", original_shift: "Night Shift (22:00 - 06:00)", corrected_shift: "Evening Shift (14:00 - 22:00)", reason: "Assigned wrong shift", status: "Pending", applied_on: "2026-08-04T10:05:00" },
  { id: 1, request_id: "SCR-2026-0001", date: "2026-08-02", correction_type: "shift_timing", original_shift: "General Shift (10:00 - 19:00)", corrected_shift: "Standard Shift (09:30 - 18:30)", reason: "Timing corrected", status: "Approved", applied_on: "2026-08-02T19:10:00" },
];
